import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

// UX / UI Reviewer packet, a shareable printable page.
// Kept text-first and dark-mode friendly (uses --pr-* variables).
// The owner sends this URL to their design/UX team.

const styles = `
  .rg-quickref { background: var(--pr-card); border: 1px solid var(--pr-line); border-radius: 16px; padding: 18px 20px; margin: 22px 0 6px; }
  .rg-owner-tag { display: inline-block; font-size: 10px; letter-spacing: .18em; text-transform: uppercase; background: #2A1E5C; color: #fff; border-radius: 999px; padding: 3px 9px; vertical-align: middle; margin-right: 8px; }
  .rg-page {
    background: var(--pr-bg);
    color: var(--pr-ink);
    min-height: 100vh;
    padding: 48px 20px 96px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }
  .rg-wrap { max-width: 780px; margin: 0 auto; }
  .rg-eyebrow {
    display: inline-block;
    background: var(--pr-tint-gold);
    color: var(--pr-accent-gold-text);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 6px 12px;
    border-radius: 999px;
    margin-bottom: 18px;
  }
  .rg-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin: 6px 0 28px;
  }
  .rg-btn {
    display: inline-block;
    padding: 10px 18px;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 700;
    text-decoration: none;
    cursor: pointer;
    border: 1px solid var(--pr-ink);
    background: transparent;
    color: var(--pr-ink);
    transition: background 0.15s, color 0.15s, transform 0.15s;
  }
  .rg-btn:hover { background: var(--pr-ink); color: var(--pr-bg); transform: translateY(-1px); }
  .rg-btn-gold {
    background: var(--pr-gold);
    color: #2A1E5C;
    border-color: var(--pr-gold);
  }
  .rg-btn-gold:hover { background: #E8B841; color: #2A1E5C; }
  .rg-h1 {
    font-family: "Fraunces", Georgia, serif;
    font-size: 40px;
    line-height: 1.1;
    margin: 0 0 12px;
    color: var(--pr-ink);
  }
  .rg-lede {
    font-size: 17px;
    line-height: 1.55;
    color: var(--pr-ink2);
    margin: 0 0 12px;
    border-left: 3px solid var(--pr-gold);
    padding-left: 16px;
  }
  .rg-h2 {
    font-family: "Fraunces", Georgia, serif;
    font-size: 26px;
    margin: 48px 0 12px;
    color: var(--pr-ink);
  }
  .rg-h3 {
    font-size: 18px;
    font-weight: 700;
    margin: 28px 0 8px;
    color: var(--pr-ink);
  }
  .rg-p, .rg-ol li, .rg-ul li {
    font-size: 15px;
    line-height: 1.65;
    color: var(--pr-ink2);
  }
  .rg-ol, .rg-ul { padding-left: 22px; margin: 12px 0; }
  .rg-ol li, .rg-ul li { margin-bottom: 8px; }
  .rg-persona, .rg-preflight, .rg-directive {
    border-radius: 14px;
    padding: 22px 26px;
    margin: 18px 0;
    font-size: 15.5px;
    line-height: 1.65;
    color: var(--pr-ink2);
  }
  .rg-persona {
    background: var(--pr-tint-ink);
    border: 1px solid var(--pr-card-line);
  }
  .rg-preflight {
    background: var(--pr-tint-red);
    border: 2px solid var(--pr-red);
  }
  .rg-preflight .rg-h3 {
    color: var(--pr-red);
    margin-top: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 20px;
  }
  .rg-preflight ol { padding-left: 22px; }
  .rg-preflight ol li b { color: var(--pr-ink); }
  .rg-directive {
    background: var(--pr-tint-gold);
    border: 2px solid var(--pr-gold);
  }
  .rg-directive .rg-h3 {
    color: var(--pr-accent-gold-text);
    margin-top: 0;
    font-size: 20px;
  }
  .rg-directive .example {
    background: var(--pr-card);
    border: 1px solid var(--pr-line);
    border-radius: 8px;
    padding: 14px 18px;
    margin-top: 12px;
    font-size: 14px;
    line-height: 1.6;
  }
  .rg-directive .bad { color: var(--pr-red); }
  .rg-directive .good { color: var(--pr-accent-green-text); }
  .rg-dual {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin: 18px 0;
  }
  @media (max-width: 620px) { .rg-dual { grid-template-columns: 1fr; } }
  .rg-track {
    background: var(--pr-card);
    border: 1px solid var(--pr-line);
    border-radius: 12px;
    padding: 20px 22px;
  }
  .rg-track h4 {
    margin: 0 0 6px;
    font-size: 16px;
    color: var(--pr-ink);
  }
  .rg-track .badge {
    display: inline-block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 4px 10px;
    border-radius: 999px;
    background: var(--pr-tint-ink);
    color: var(--pr-ink);
    margin-bottom: 10px;
  }
  .rg-table {
    width: 100%;
    border-collapse: collapse;
    margin: 18px 0 8px;
    font-size: 14px;
    background: var(--pr-card);
    border: 1px solid var(--pr-line);
    border-radius: 10px;
    overflow: hidden;
  }
  .rg-table th, .rg-table td {
    text-align: left;
    padding: 10px 14px;
    border-bottom: 1px solid var(--pr-line);
    color: var(--pr-ink);
  }
  .rg-table th {
    background: var(--pr-bg-alt);
    color: var(--pr-ink);
    font-weight: 700;
    font-size: 12.5px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .rg-table tr:last-child td { border-bottom: none; }
  .rg-code {
    display: block;
    background: var(--pr-ink-surface);
    color: #FAF7F0;
    padding: 14px 18px;
    border-radius: 10px;
    font-family: "SF Mono", Consolas, monospace;
    font-size: 13px;
    line-height: 1.7;
    margin: 12px 0;
    white-space: pre;
    overflow-x: auto;
  }
  .rg-note {
    background: var(--pr-tint-warm);
    border: 1px solid var(--pr-card-line);
    border-left: 3px solid var(--pr-gold);
    padding: 14px 18px;
    border-radius: 8px;
    font-size: 14.5px;
    line-height: 1.6;
    color: var(--pr-ink2);
    margin: 16px 0;
  }
  .rg-hr {
    border: none;
    border-top: 1px dashed var(--pr-line);
    margin: 40px 0;
  }
  .rg-footer {
    margin-top: 56px;
    padding-top: 24px;
    border-top: 1px solid var(--pr-line);
    font-size: 13px;
    color: var(--pr-muted);
    text-align: center;
  }
  @media print {
    .rg-page { background: #fff; padding: 24px 0; color: #111; }
    .rg-wrap { max-width: 100%; }
    .rg-actions { display: none !important; }
    .rg-h2 { page-break-after: avoid; }
    .rg-table, .rg-code, .rg-note, .rg-persona, .rg-preflight, .rg-directive, .rg-track { page-break-inside: avoid; }
    * { color: #111 !important; background: #fff !important; }
    .rg-preflight, .rg-directive { border: 2px solid #111 !important; }
    .rg-code { background: #f4f4f4 !important; color: #111 !important; border: 1px solid #ddd; }
  }
`;

export default function ReviewerGuidePage() {
  const location = useLocation();
  // /reviewer-guide is the clean reviewer-facing packet; ?owner=1 adds the host-only sections.
  const owner = new URLSearchParams(location.search).get("owner") === "1";

  useEffect(() => {
    document.title = owner ? "UX Reviewer Packet (owner view), Payday Relay" : "UX Reviewer Packet, Payday Relay";
  }, [owner]);

  const handlePrint = () => window.print();

  return (
    <div className="rg-page" data-testid={owner ? "reviewer-guide-page-owner" : "reviewer-guide-page"}>
      <style>{styles}</style>
      <div className="rg-wrap">
        <span className="rg-eyebrow">{owner ? "Owner view · UX Review" : "UX / UI Review · Reviewer Packet"}</span>
        <h1 className="rg-h1">Payday Relay, UX / UI Reviewer Packet</h1>
        <p className="rg-lede">
          {owner ? (
            <>A 60–90 minute walk-through you can hand to a reviewer who has 1–2 days to give feedback.
            They never need to see the builder interface, and they'll experience the full
            <b> Free 4-Relay Sprint → Season Pass</b> handoff, plus a live Season week end-to-end so
            they can confirm the app really does keep the master 52-week sheet in sync, without
            waiting 28 days for real time to pass.
            <span style={{ display: "block", marginTop: 8, fontSize: 13.5, color: "var(--pr-muted)" }}>
              Reviewers get the clean version at <code>/reviewer-guide</code>, the sections marked <b>Owner</b> below only appear here.
            </span></>
          ) : (
            <>Thank you for taking a look. This is a 60–90 minute walk-through of Payday Relay, you'll
            experience the full <b>Free 4-Relay Sprint → Season Pass</b> journey without waiting 28 real
            days, then run a live week inside the Season Pass to prove the master 52-week Google Sheet
            stays in sync. Your host fast-forwards time for you at two points, and the script tells
            you exactly when to ping them.</>
          )}
        </p>
        <div className="rg-actions">
          <button className="rg-btn rg-btn-gold" onClick={handlePrint} data-testid="reviewer-guide-print-btn">
            🖨️ Download / Print as PDF
          </button>
          <Link className="rg-btn" to="/reviewer-feedback" data-testid="reviewer-guide-feedback-link">
            💬 Submit feedback →
          </Link>
        </div>

        {!owner && (
          <div className="rg-quickref" data-testid="reviewer-guide-quickref">
            <h3 className="rg-h3" style={{ marginTop: 0 }}>At a glance</h3>
            <table className="rg-table" style={{ marginBottom: 0 }}>
              <tbody>
                <tr><td>Time needed</td><td><b>60–90 min</b> across a laptop <i>and</i> a phone (includes a live Season-week sync test)</td></tr>
                <tr><td>Start here</td><td><code>https://paydayrelay.com/beta</code></td></tr>
                <tr><td>You'll need</td><td>A Google account (a throwaway one is fine) + a unique reviewer email</td></tr>
                <tr><td>Test card</td><td><code>4242 4242 4242 4242</code> · any future expiry · CVC <code>123</code> · any ZIP. No real money moves</td></tr>
                <tr><td>Optional shortcut</td><td><b>Only if your host asks you to.</b> Promo code <code>DOVELY87</code> at checkout unlocks the paid Season Pass at 100% off, for auditing the paid tier directly. Skip this by default, the main review path is the Free Sprint → Season Pass handoff.</td></tr>
                <tr><td>Two pings to your host</td><td>"Sheet verified, ready for midway seed" · "Ready to convert"</td></tr>
                <tr><td>Feedback goes to</td><td><Link to="/reviewer-feedback" style={{ color: "var(--pr-ink)", textDecoration: "underline" }}>paydayrelay.com/reviewer-feedback</Link></td></tr>
              </tbody>
            </table>
          </div>
        )}

        {/* ============= PRE-FLIGHT CHECKLIST ============= */}
        <div className="rg-preflight" data-testid="reviewer-guide-preflight">
          <h3 className="rg-h3">🚨 Pre-Flight Checklist, read before you start</h3>
          <p className="rg-p" style={{ marginTop: 0 }}>
            Do not skip these three items. Getting them right up front is the difference between a
            useful review and a broken one.
          </p>
          <ol className="rg-ol">
            <li>
              <b>Google Account Required.</b> You must use a Google account to test this system, 
              the app creates and syncs a Google Sheet in your Drive. If you don't have a Google
              account, create a dummy one for this audit (~2 min). When Google asks for permissions,
              make sure the <b>Google Sheets</b> box is ticked, the app can't build your sheet without it.
            </li>
            <li>
              <b>The Master Sync.</b> Right after checkout you're signed in automatically and walked
              through three steps: <b>Step 1 Calculate</b> (the Relay Calculator, inside the app) →{" "}
              <b>Step 2 Sync your sheet</b> (Google Drive authorization. This creates your permanent{" "}
              <b>Payday Relay Legend</b>, your Google Sheet, pre-loaded with your numbers) →{" "}
              <b>Step 3 Log</b>. <b>Do not skip Step 2.</b> If you don't finish the sync, you're only
              reviewing half the product. (A backup "set up your sheet" email with the same link also arrives.)
            </li>
            <li>
              <b>Verify before you race.</b> Open <a href="https://drive.google.com" target="_blank" rel="noreferrer" style={{ color: "var(--pr-ink)", textDecoration: "underline" }}>drive.google.com</a> in a new tab and confirm a
              file called something like <b>"Payday Relay, 4-Relay Challenge"</b> is now sitting in
              your Drive. Open it. You should see a laid-out Legend / Scoreboard / weekly tabs. Only
              once that's confirmed, tell your host "sheet verified, ready for midway seed."
            </li>
          </ol>
          <p className="rg-p" style={{ marginBottom: 0, fontSize: 13.5 }}>
            <b>Why:</b> the real value of Payday Relay is the app + Google Sheet duo. Reviewing only
            the app means missing half the product.
          </p>
        </div>

        {/* ============= HOW TO GIVE FEEDBACK ============= */}
        <div className="rg-directive" data-testid="reviewer-guide-directive">
          <h3 className="rg-h3">✍️ How to Give Feedback, the Founder's Rule</h3>
          <p className="rg-p" style={{ marginTop: 0 }}>
            To make sure your feedback is actionable (and can be shipped by our AI builder without
            guesswork), please frame <b>every issue</b> as three parts:
          </p>
          <ol className="rg-ol">
            <li><b>The Problem</b>, what specifically is wrong or confusing.</li>
            <li><b>The Impact</b>, who it hurts, how badly, and where in the flow.</li>
            <li>
              <b>The Suggested Fix</b>, what to change. <b>Bonus:</b> phrase the fix as a builder
              prompt (e.g. <i>"Move the Log button to the top left and increase its size by 20%."</i>).
            </li>
          </ol>
          <div className="example">
            <div className="bad">
              <b>Vague (unusable):</b> "The dashboard feels cluttered."
            </div>
            <div className="good" style={{ marginTop: 10 }}>
              <b>Actionable directive:</b> "The PTO Bank card lives on the same screen as the daily
              log, which competes for attention when I'm just trying to enter today's income.{" "}
              <i>Fix:</i> Move the PTO Bank to a separate tab under /app/timeoff so the daily
              entry screen is quieter."
            </div>
          </div>
          <p className="rg-p" style={{ marginBottom: 0, fontSize: 13.5 }}>
            "Feelings" tell us something's off, <b>Directives</b> tell us how to fix it. Give us
            directives whenever you can.
          </p>
        </div>

        {owner && (<>
        <h2 className="rg-h2"><span className="rg-owner-tag">Owner</span> Before the review starts (5 min)</h2>
        <ol className="rg-ol">
          <li>
            <b>Send them the private URL:</b>{" "}
            <code>https://paydayrelay.com/beta</code>
            <br />
            <span style={{ color: "var(--pr-muted)", fontSize: 13 }}>
              Pre-launch mode is on. <code>/</code> shows the "almost here" waitlist page.
              <code> /beta</code> is where the real product lives. Do not put <code>/beta</code> in any public copy.
            </span>
          </li>
          <li>
            <b>Give each reviewer a unique reviewer email</b>, either their Gmail with a <code>+</code> alias,
            or a mailinator inbox they'll check. Different address per reviewer, so their data doesn't collide.
          </li>
          <li>
            <b>Bookmark this, you'll click it two or three times per reviewer:</b>
            <div className="rg-code">https://paydayrelay.com/api/admin/subscribers?key=YOUR_ADMIN_KEY#race-sim</div>
            That's the admin Race Simulator. See "Owner controls" at the bottom.
          </li>
        </ol>
        </>)}

        <h2 className="rg-h2">{owner ? "Reviewer persona, hand this to them verbatim" : "Your persona for this review"}</h2>
        <div className="rg-persona">
          <b>You are Sam Rivera, 34, freelance graphic designer.</b>
          <br /><br />
          You work with 3–5 recurring clients. Your income swings between{" "}
          <b>$2,000 and $8,000 a month</b> and lands whenever a client feels like paying. You have{" "}
          <b>$8,000 in credit card debt</b> you want cleared in 12 months, want to build a{" "}
          <b>$3,000 emergency fund</b>, and would love to take <b>two weeks off</b> without your bank
          account panicking. You work about <b>4 days a week</b>. You've tried YNAB and spreadsheets,
          and quit both.
          <br /><br />
          A friend just sent you a link and said <i>"this one's built for people like us."</i> You're
          skeptical but curious. You have ADHD traits that make open-ended work harder to structure. You want to know within 30 seconds
          whether this is worth your time.
        </div>

        <h3 className="rg-h3">Sam's numbers, use these in the calculator</h3>
        <table className="rg-table">
          <thead><tr><th>Field</th><th>Value</th></tr></thead>
          <tbody>
            <tr><td>Weekly take-home target</td><td><b>$1,500</b> (defaults pre-fill, leave it)</td></tr>
            <tr><td>Days worked per week</td><td><b>4</b></td></tr>
            <tr><td>Weeks off per year</td><td><b>2</b></td></tr>
            <tr><td>Federal tax rate</td><td><b>25%</b></td></tr>
            <tr><td>Debt to clear</td><td><b>$8,000</b> over <b>12 months</b></td></tr>
            <tr><td>Savings goal</td><td><b>$3,000</b></td></tr>
            <tr><td>Payday delay</td><td><b>7 days</b> (clients pay a week after invoice)</td></tr>
            <tr><td>Preferred name</td><td><b>Sam</b></td></tr>
          </tbody>
        </table>

        <hr className="rg-hr" />

        {/* ============= THE DUAL-TRACK TEST ============= */}
        <h2 className="rg-h2">📱 💻 The Dual-Track Test (non-negotiable)</h2>
        <p className="rg-p">
          Payday Relay lives across two devices: the daily habit (logging) happens on a phone, the
          strategic view (Scoreboard, Resource Library, Google Sheet) happens on a laptop. Please
          test <b>both</b>, and also test the <b>handoff between them</b>.
        </p>
        <div className="rg-dual">
          <div className="rg-track">
            <span className="badge">Track 1</span>
            <h4>📱 Mobile, The Daily Habit</h4>
            <p className="rg-p" style={{ marginTop: 0, fontSize: 14 }}>
              Log in via your smartphone browser. Do all your <b>daily logging</b> and time-off
              tagging on mobile.
            </p>
            <ul className="rg-ul" style={{ fontSize: 13.5, marginBottom: 0 }}>
              <li>Is the Log Income button easy to hit with one thumb?</li>
              <li>Does any text overlap or get cut off?</li>
              <li>Does the Caddy AI feel fast enough?</li>
              <li>Try installing the app to your home screen, is that flow clear?</li>
              <li>Open the <b>More</b> tab at the bottom, is it obvious that your goal, Time Off & PTO, and Monthly Wrap-Up live there?</li>
            </ul>
          </div>
          <div className="rg-track">
            <span className="badge">Track 2</span>
            <h4>💻 Desktop, The Big Picture</h4>
            <p className="rg-p" style={{ marginTop: 0, fontSize: 14 }}>
              Log in via your laptop. Do all your <b>strategic reviewing</b> on desktop, the
              Yearly Scoreboard, Resource Library, and your Google Sheet.
            </p>
            <ul className="rg-ul" style={{ fontSize: 13.5, marginBottom: 0 }}>
              <li>Is the information architecture clear on the Scoreboard?</li>
              <li>Does the Resource Library feel authoritative and professional?</li>
              <li>Open your Google Sheet, do the numbers you logged on mobile actually appear there?</li>
              <li>Are the tabs in the Sheet organized in an order that makes sense?</li>
            </ul>
          </div>
        </div>
        <h3 className="rg-h3">🔄 Track 3, The Handoff (this is the critical one)</h3>
        <p className="rg-p">
          After you've logged wins on your phone, open your laptop, visit <code>/app/scoreboard</code>,
          and then open your Google Sheet. Answer:
        </p>
        <ul className="rg-ul">
          <li>Do the numbers you entered on mobile show up correctly on the desktop Scoreboard?</li>
          <li>Do the same numbers show up correctly in your Google Sheet?</li>
          <li>Is there any delay, mismatch, or need to refresh?</li>
          <li>When you flip between "app" and "sheet," does it feel like one system or two?</li>
        </ul>

        <hr className="rg-hr" />

        <h2 className="rg-h2">{owner ? "What the reviewer does (the script)" : "The script"}</h2>

        <h3 className="rg-h3">Part 1, First impression (5 min, no login, laptop)</h3>
        <ol className="rg-ol">
          <li>Open <code>https://paydayrelay.com/beta</code>.</li>
          <li>Scroll top to bottom <b>once</b>, no clicking. Then come back up.</li>
          <li>Note: what do you think this app IS in one sentence? Who is it for? Which section stopped your scroll?</li>
          <li>Click <b>Compare</b> and <b>Features</b> from the top nav. Skim each.</li>
          <li>Return to <code>/beta</code>, click <b>Start Your Free Sprint</b>.</li>
        </ol>

        <h3 className="rg-h3">Part 2, Sign up + Google Sheet sync (10 min, laptop)</h3>
        <ol className="rg-ol">
          <li>Enter your reviewer email at the Free 4-Relay Sprint checkout.</li>
          <li>
            Use this <b>Stripe test card</b>. No real money moves:
            <div className="rg-code">{`Card:  4242 4242 4242 4242
Exp:   any future date, e.g. 12/34
CVC:   123
ZIP:   any 5 digits, e.g. 90210`}</div>
          </li>
          <li>After checkout you're <b>signed in automatically</b> and land on <b>Step 1 · Calculate</b>, the Relay Calculator inside the app. Use Sam's numbers. On the results, click <b>"Lock in my relay numbers → next: sync your sheet"</b> (your results PDF is emailed to you).</li>
          <li><b>Step 2 · Sync your sheet</b>. You land on the sheet setup, prefilled with your numbers. Click <b>Connect Google</b>, authorize, set your start date and payday rhythm, and wait for the sheet to build.</li>
          <li><b>Verify</b> (Pre-Flight step 3), open drive.google.com in a new tab and confirm your Payday Relay sheet is there. Open it. Skim the Legend tab.</li>
          <li>Click <b>"Step 3 · Start logging your race →"</b>. You should land on the dashboard, Day 1 of your Sprint. (If you ever get signed out, use the magic-link email or <code>/login</code>.)</li>
        </ol>

        <div className="rg-note">
          <b>Ping #1, message your host:</b> "I'm signed in, Google is connected, sheet is verified, ready
          for midway seed." Your host fast-forwards your Sprint by 27 days so you can log wins into the past
          four weeks without waiting. Give it a minute, then continue.
        </div>

        <h3 className="rg-h3">Part 3, Live the Sprint (mobile-first, ~15 min)</h3>
        <p className="rg-p" style={{ fontStyle: "italic" }}>
          Now switch to your phone. Sign in on mobile using the same magic-link email.
        </p>
        <ol className="rg-ol">
          <li>Refresh <code>/app</code>. You should be near the end of <b>Relay 4</b> (Day 27) with a "your Sprint converts tomorrow" banner.</li>
          <li>
            Log wins into past days <b>on your phone</b>. Sample earnings to sprinkle across the four weeks:
            <ul className="rg-ul">
              <li>Week 1: $420, $380, $625 (3 working days)</li>
              <li>Week 2: $1,180 (one big invoice day), plus one day off tagged</li>
              <li>Week 3: $500, $310, $450, $290 (a grind week)</li>
              <li>Week 4: $700 today so far</li>
            </ul>
          </li>
          <li>Try <b>tagging a day off</b> and a <b>PTO day</b> from <code>/app/timeoff</code>.</li>
          <li>Poke every mobile section, <b>Daily · Scoreboard · Journal · Resources · More</b>. Some are shown as grayed <b>Season Pass previews</b> in Sprint mode, does that feel intentional, or broken?</li>
          <li>On the Daily screen, note the order: relay line (payout date) → coach message → the single <b>This week</b> module (what's left → log today → day plan). Does anything still feel like clutter? Then try the <b>Caddy AI chat</b> (bottom-right bubble). Ask it: <i>"do I need to file taxes if I'm 1099?"</i></li>
          <li>
            <b>Switch to your laptop.</b> Open <code>/app/scoreboard</code>. Do all the mobile wins
            you logged show up correctly here?
          </li>
          <li>
            <b>Open your Google Sheet in a new tab.</b> Do the same numbers appear in the sheet? Any
            missing? Any wrong days? <i>Note anything that doesn't match. This is one of the most
            important pieces of feedback.</i>
          </li>
        </ol>

        <div className="rg-note">
          <b>Ping #2, message your host:</b> "Ready to convert." Your host flips you to an active Season Pass
          member, which puts the <b>Season Setup gate</b> on top of your <code>/app</code>.
        </div>

        <h3 className="rg-h3">Part 4, The conversion moment (laptop, ~10 min)</h3>
        <ol className="rg-ol">
          <li>Refresh <code>/app</code>. A full-screen gate appears: <i>Keep my Sprint settings</i> OR <i>Recalibrate my Relay</i>.</li>
          <li>
            <b>Do BOTH paths, one at a time.</b> Ask your host to reset you between attempts (it takes them ten seconds).
            <ul className="rg-ul">
              <li><b>Path A, Keep My Sprint Settings.</b> Confirm and start. Watch the 52-week board unlock.</li>
              <li><b>Path B, Recalibrate.</b> You'll be taken to the full Relay Calculator. Use Sam's numbers. On the results page, click <b>"Start My Payday Relay Race →"</b>. Confirm on the review card.</li>
            </ul>
          </li>
          <li>
            Now explore every previously-locked section:
            <ul className="rg-ul">
              <li><b>Scoreboard</b>, all 52 relays visible.</li>
              <li><b>Time Off & PTO</b> at <code>/app/timeoff</code>, tag a full week off and choose paid (from your PTO bank) or unpaid.</li>
              <li><b>Resource Library</b> at <code>/app/resources</code>, Money Management · Productivity · Money Systems · Business Inspiration. Does it feel curated and credible?</li>
              <li><b>Quarterly Tax Day</b> at <code>/app/reset</code>. When a window is open it opens itself once and the green pill reads <i>Quarterly tax day open · Recalibrate →</i>. Step 1: tick every "how did it feel" word that fits (as many as you like). Step 3: hit <b>Re-run the Relay Calculator</b>, change one input, click <b>Lock in my new numbers → back to quarterly tax day</b>, confirm the target came back with your earlier answers intact, then <b>Lock in my reset</b>. The pill should now read <i>✓ Q# numbers set</i>, your answers show in the Journal tagged <i>Q# recalibration</i>, and the Scoreboard gains a <b>How each quarter felt</b> card.</li>
              <li><b>Monthly Wrap-Up</b> at <code>/app/monthly</code>.</li>
            </ul>
          </li>
          <li>
            <b>Open your Google Sheet again.</b> Did the season conversion update anything in the sheet
            (new tabs, new labels)? Does the sheet still feel accurate?
          </li>
        </ol>

        <h3 className="rg-h3">Part 5, Live a full Season week (mobile + laptop + sheet, ~15 min)</h3>
        <p className="rg-p">
          The Sprint is the appetizer. The <b>Season Pass is the whole meal</b>, and its master
          artifact is your <b>52-week Payday Relay Google Sheet</b>. The point of this section is to
          confirm the app-to-master-sheet sync actually works when someone lives inside the paid
          product. Not just during the Sprint. Please do this whole section slowly.
        </p>
        <ol className="rg-ol">
          <li>
            <b>Locate the master sheet.</b> Open <a href="https://drive.google.com" target="_blank" rel="noreferrer" style={{ color: "var(--pr-ink)", textDecoration: "underline" }}>drive.google.com</a>. After conversion you should
            see a <b>52-week Payday Relay</b> sheet in your Drive (it may be the same file relabeled,
            or a new one alongside the Sprint sheet). Note which, either is a valid signal, and
            we want to know how it landed. Open it. Skim the <b>Legend</b>, <b>Scoreboard</b>, and
            the first weekly tab.
          </li>
          <li>
            <b>Log a full week on your phone.</b> Pretend it's a real work week. On mobile,
            <code>/app</code>, log wins for the current relay week, spread them across at least
            3 days, some smaller and some larger. Suggested pattern (adjust to whatever feels
            realistic):
            <ul className="rg-ul">
              <li>Monday: <b>$310</b></li>
              <li>Tuesday: <b>$480</b></li>
              <li>Thursday: <b>$625</b></li>
              <li>Friday: <b>$260</b></li>
            </ul>
            After each log, watch the daily screen update (relay line, coach message, "what's left").
          </li>
          <li>
            <b>Tag time off inside the week.</b> On mobile <code>/app/timeoff</code>, tag one day as
            an <b>unpaid day off</b> and (if your PTO bank has room) tag a second day as a <b>paid PTO day</b>.
            Note whether the daily target and weekly-left number visibly adjust.
          </li>
          <li>
            <b>Clear the relay.</b> Log enough on the last day to cross your weekly target. Do you
            get a visible "relay cleared" moment (line turns green, a Caddy congrats, a streak
            update)? Screenshot anything that feels off.
          </li>
          <li>
            <b>Switch to your laptop</b> and open <code>/app/scoreboard</code>. The current relay
            should be marked cleared. Any Sprint weeks you already logged should still be there.
            Do the numbers match what your phone shows?
          </li>
          <li>
            <b>Open the master 52-week sheet.</b> This is the sync test that matters most:
            <ul className="rg-ul">
              <li>Find <b>this week's tab</b>. Are your Mon/Tue/Thu/Fri amounts on the correct days?</li>
              <li>Is the <b>weekly total</b> right (sum of what you logged in the app)?</li>
              <li>Is the relay marked <b>cleared</b> in the sheet, flag, checkmark, or green cell, the same as in the app?</li>
              <li>If you took a paid PTO day, did the <b>PTO bank</b> shrink by that day's target on the sheet?</li>
              <li>Does the <b>Scoreboard tab</b> in the sheet update the running totals for the year?</li>
              <li>Any tabs missing, misnamed, or empty when they shouldn't be?</li>
            </ul>
          </li>
          <li>
            <b>Do one more sanity check on your phone.</b> Log one <i>extra</i> $75 win on today.
            Refresh the sheet. Did it appear? How long did it take?
          </li>
        </ol>
        <div className="rg-note">
          <b>Why this test is important:</b> the Sprint proves the onboarding works. This Season-week
          test proves the <b>real day-to-day product</b> works. If any number is missing, delayed,
          on the wrong day, or on the wrong tab. That is the single most important thing to write up.
          Flag it with a screenshot of the app screen AND the sheet cell.
        </div>

        <hr className="rg-hr" />

        <h2 className="rg-h2">Scorecard</h2>
        <p className="rg-p">
          Rate 1 (broken / confusing) → 5 (crystal clear and delightful). For anything below a 4 or
          above a 4, write one line explaining <i>why</i>, and where possible frame it as a
          Problem · Impact · Fix directive.
        </p>
        <p className="rg-p">
          <b>Ready to submit?</b>{" "}
          <Link to="/reviewer-feedback" style={{ color: "var(--pr-ink)", textDecoration: "underline" }}>
            Fill in the feedback form here →
          </Link>{" "}
          (or use this scorecard as a scratchpad first).
        </p>

        <h3 className="rg-h3">A. Homepage / landing (/beta)</h3>
        <ScoreTable rows={[
          "Hero, is the pitch clear in one sentence?",
          "Who this is for is obvious",
          "The \"how the race works\" section",
          "Pricing / Sprint offer clarity",
          "Testimonial / trust signals",
          "Compare page",
          "Features page",
          "Overall visual polish",
        ]} />

        <h3 className="rg-h3">B. Sign-up + Google Sheet sync</h3>
        <ScoreTable rows={[
          "Free Sprint CTA feels safe (not a trick)",
          "Checkout is calm and trustworthy",
          "Magic-link email tone",
          "Google Drive authorization step (clear? scary?)",
          "The moment the sheet appears in your Drive, is it obvious it worked?",
          "First landing on /app, is Step 1 obvious?",
        ]} />

        <h3 className="rg-h3">C. Mobile experience (Track 1)</h3>
        <ScoreTable rows={[
          "Log Income button. One-thumb reachable?",
          "Text/labels, legible with no overlap?",
          "Dashboard hierarchy on mobile (Today / Week / More)",
          "Time Off / PTO tagging on mobile",
          "Mobile bottom nav, clear what each icon does?",
          "Caddy AI chat, fast enough on mobile?",
          "Add-to-Home-Screen flow (if you tried it)",
          "Menu drawer, is it obvious what lives there?",
        ]} />

        <h3 className="rg-h3">D. Desktop experience (Track 2)</h3>
        <ScoreTable rows={[
          "Sidebar / desktop layout",
          "Yearly Scoreboard, information architecture",
          "Resource Library, feels authoritative?",
          "Journal + Monthly Wrap-Up on desktop",
          "Google Sheet organization (Legend, Scoreboard, weekly tabs)",
          "Season Setup gate on desktop",
        ]} />

        <h3 className="rg-h3">E. The handoff (Track 3, cross-device)</h3>
        <ScoreTable rows={[
          "Mobile-logged wins appear correctly on desktop Scoreboard",
          "Mobile-logged wins appear correctly in the Google Sheet",
          "Google Sheet numbers match app numbers (no drift, no delay)",
          "Does moving between app and sheet feel like ONE system?",
          "Where (if anywhere) did the connection feel broken?",
        ]} />

        <h3 className="rg-h3">F. The Sprint experience (Days 1–28)</h3>
        <ScoreTable rows={[
          "Locked sections during the Sprint, motivating vs. frustrating",
          "Day-27 \"converts tomorrow\" message, honest, spammy, or scary?",
          "The overall Sprint tone, does it earn trust?",
        ]} />

        <h3 className="rg-h3">G. The conversion moment</h3>
        <ScoreTable rows={[
          "The Season Setup gate copy",
          "\"Keep my settings\" path clarity",
          "\"Recalibrate\" path, using the calculator",
          "Emotional landing after the gate closes",
        ]} />

        <h3 className="rg-h3">H. The master 52-week sheet sync (Season Pass)</h3>
        <ScoreTable rows={[
          "Master 52-week sheet clearly appeared after conversion (renamed vs new file)",
          "Season-week wins land on the correct days in the current-week tab",
          "Weekly total in the sheet matches the app's weekly total",
          "Relay-cleared status in the sheet matches the app",
          "Paid PTO day debited the PTO bank on the sheet",
          "Scoreboard tab totals update as the week progresses",
          "New win logged mid-week appears on the sheet quickly (<1 min feels fine)",
          "Any tabs missing, misnamed, or empty",
        ]} />

        <h3 className="rg-h3">I. Emotional / brand</h3>
        <ScoreTable rows={[
          "Does the \"relay race\" metaphor stay useful or get tiring?",
          "Does it feel built for freelancers or generic finance?",
          "Does it feel honest about money?",
          "Would you tell a friend to try the Sprint?",
        ]} />

        <h3 className="rg-h3">The single big cross-device question</h3>
        <div className="rg-note">
          On a scale of <b>1–10</b>, how <b>seamlessly</b> could you move from your phone to your
          laptop (and to your Google Sheet) to manage your Relay?
          <br />
          <b>Where did the connection feel broken?</b>
        </div>

        <h3 className="rg-h3">One-paragraph summary</h3>
        <p className="rg-p" style={{ fontStyle: "italic" }}>
          In 4–6 sentences: what's the strongest thing about this app, what's the weakest thing,
          and if you had to fix ONE screen tomorrow to move the needle, which one and why?
        </p>

        <h3 className="rg-h3">Open teardown</h3>
        <p className="rg-p">
          Bullet list of everything else, copy nits, ideas, weird moments, screenshots, anything.
          <b> Please frame every issue as Problem · Impact · Fix</b> (see the Founder's Rule above).
        </p>

        <div className="rg-actions" style={{ marginTop: 24 }}>
          <Link className="rg-btn rg-btn-gold" to="/reviewer-feedback" data-testid="reviewer-guide-submit-bottom">
            💬 Submit your feedback →
          </Link>
          <button className="rg-btn" onClick={handlePrint}>🖨️ Print / Save as PDF</button>
        </div>

        {owner && (<>
        <hr className="rg-hr" />

        <h2 className="rg-h2"><span className="rg-owner-tag">Owner</span> Owner controls</h2>
        <p className="rg-p">
          Everything you need is on one admin page:{" "}
          <code>https://paydayrelay.com/api/admin/subscribers?key=YOUR_ADMIN_KEY#race-sim</code>
        </p>
        <p className="rg-p">Type the reviewer's email into the Race Simulator box, then click:</p>
        <table className="rg-table">
          <thead><tr><th>Button</th><th>What it does</th><th>When to click</th></tr></thead>
          <tbody>
            <tr>
              <td>📅 Reviewer mode, Sprint started 27 days ago</td>
              <td>Backdates their Sprint 27 days, sets $1,500 / 4 days, trial end +20h, fires warning email. Response includes a <code>/sheet-setup?token=…</code> URL you can hand them for Google connection.</td>
              <td>After "I'm signed in, Google is connected, ready to skip ahead"</td>
            </tr>
            <tr>
              <td>🏁 Convert now, show the gate</td>
              <td>Flips them to active Season Pass + puts the Season Setup gate on /app</td>
              <td>After they've done the Sprint tour</td>
            </tr>
            <tr>
              <td>🔄 Reset to fresh Sprint</td>
              <td>Back to Day 1 of a fresh Sprint, gate cleared</td>
              <td>Between reviewers, or to let one reviewer redo the other gate path</td>
            </tr>
          </tbody>
        </table>
        <div className="rg-note">
          The simulator refuses any account that has a real Stripe subscription attached. You cannot
          accidentally break a paying customer.
        </div>

        <h3 className="rg-h3">Per-reviewer checklist</h3>
        <ol className="rg-ol">
          <li>Send them <code>/reviewer-guide</code> (the clean packet) + a unique reviewer email.</li>
          <li>Wait for their "I'm signed in, Google is connected, sheet verified" ping.</li>
          <li>Click <b>Sprint started 27 days ago</b>.</li>
          <li>Wait for their "ready to convert" ping.</li>
          <li>Click <b>Convert now</b>.</li>
          <li>Optional: <b>Reset</b> → <b>Convert</b> again so they experience the second gate path.</li>
          <li>Check the reviewer feedback admin page at <code>/api/admin/reviewer-feedback?key=…</code></li>
        </ol>
        </>)}

        <div className="rg-footer">
          Payday Relay, Reviewer Packet{owner ? " · owner view" : ""} ·{" "}
          <a href="/beta" style={{ color: "var(--pr-ink)" }}>go to /beta</a> ·{" "}
          <Link to="/reviewer-feedback" style={{ color: "var(--pr-ink)" }}>submit feedback</Link>
        </div>
      </div>
    </div>
  );
}

function ScoreTable({ rows }) {
  return (
    <table className="rg-table">
      <thead><tr><th style={{ width: "70%" }}>Line</th><th>Rating (1–5)</th><th>Note</th></tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}><td>{r}</td><td></td><td></td></tr>
        ))}
      </tbody>
    </table>
  );
}
